<template>
  <div class="p-2 rounded-xl min-h-screen">
    <div class="bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-xl p-3 mb-4 shadow-sm border border-gray-100">
      <div class="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 class="text-sm font-bold text-gray-700 mb-1">پکیج ساز</h2>
          <p class="text-xs text-gray-500">
            چند محصول رو با تعداد دلخواه ترکیب کن و یه پکیج جدید با قیمت و موجودی مستقل بساز.
            با خرید پکیج، از موجودی تک‌تک محصولات داخلش هم کم می‌شه.
          </p>
        </div>
        <div class="flex items-center gap-2">
          <button @click="copyPublicLink" class="px-3 py-1.5 bg-gray-200 text-gray-700 text-xs rounded-lg">کپی لینک صفحه پکیج‌ها</button>
          <a :href="publicBundlesUrl" target="_blank" rel="noopener" class="px-3 py-1.5 bg-gray-800 text-white text-xs rounded-lg">مشاهده در فروشگاه</a>
        </div>
      </div>
    </div>

    <!-- فرم ساخت/ویرایش -->
    <div class="bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-xl shadow-sm border border-gray-100 mb-4 p-3">
      <h3 class="text-xs font-bold text-gray-700 mb-2">{{ editingId ? "ویرایش پکیج" : "پکیج جدید" }}</h3>

      <div class="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
        <input v-model="form.name" placeholder="نام پکیج" class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-full md:col-span-2">
        <input type="number" min="0" v-model.number="form.bundle_discount_percentage" placeholder="درصد تخفیف پکیج" class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-full">
        <input type="number" min="0" v-model.number="form.inventory" placeholder="موجودی خود پکیج" class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-full">
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
        <category-tree-select
            label="دسته بندی پکیج"
            v-model="form.categorization"
            :categories="categorizations"
        ></category-tree-select>
        <brand-select
            label="برند پکیج"
            placeholder="برند را انتخاب کنید"
            v-model="form.brand"
        ></brand-select>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3 items-center">
        <div class="flex items-center gap-2">
          <input type="file" accept="image/*" @change="onCoverChange" class="text-xs">
          <img v-if="coverPreview" :src="coverPreview" class="w-10 h-10 object-cover rounded-lg border" alt="">
        </div>
        <label class="flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
          <input type="checkbox" v-model="form.is_active">
          فعال
        </label>
      </div>
      <p class="text-[11px] text-gray-400 -mt-2 mb-3">
        تصویر شاخص اختیاریه؛ اگر آپلود نکنی، گالری پکیج به‌صورت خودکار از عکس‌های محصولات داخلش ساخته می‌شه.
      </p>

      <h4 class="text-xs font-bold text-gray-700 mb-2">محصولات داخل پکیج</h4>
      <bundle-items-select v-model="form.items"></bundle-items-select>

      <div class="bg-gray-50 rounded-lg border mt-3 p-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
        <div class="flex justify-between md:block">
          <span class="text-gray-500">جمع قیمت محصولات:</span>
          <span class="font-bold text-gray-700 mr-1">{{ formatNumber(subtotal) }} تومان</span>
        </div>
        <div class="flex justify-between md:block">
          <span class="text-gray-500">تخفیف پکیج ({{ form.bundle_discount_percentage || 0 }}٪):</span>
          <span class="font-bold text-red-500 mr-1">− {{ formatNumber(discountAmount) }} تومان</span>
        </div>
        <div class="flex justify-between md:block">
          <span class="text-gray-500">قیمت نهایی پکیج:</span>
          <span class="font-bold text-emerald-700 mr-1">{{ formatNumber(finalPrice) }} تومان</span>
        </div>
      </div>

      <div class="flex justify-end mt-3">
        <div class="flex gap-2">
          <button v-if="editingId" @click="resetForm" class="px-3 py-1.5 bg-gray-200 text-gray-700 text-xs rounded-lg">انصراف</button>
          <button @click="save" :disabled="saving" class="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded-lg">
            {{ editingId ? "ذخیره تغییرات" : "ساخت پکیج" }}
          </button>
        </div>
      </div>
    </div>

    <!-- لیست -->
    <div class="bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <table class="w-full text-right">
        <thead class="bg-gray-50">
        <tr>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase">تصویر</th>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase">نام پکیج</th>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase">تعداد اقلام</th>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase">جمع قیمت</th>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase">تخفیف</th>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase">قیمت نهایی</th>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase">موجودی</th>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase">وضعیت</th>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase"></th>
        </tr>
        </thead>
        <tbody>
        <tr v-if="loading"><td colspan="9" class="p-6 text-center text-xs text-gray-400">در حال بارگذاری...</td></tr>
        <tr v-else-if="!bundles.length"><td colspan="9" class="p-6 text-center text-xs text-gray-400">هنوز پکیجی ساخته نشده</td></tr>
        <tr v-for="item in bundles" :key="item.id" class="border-t hover:bg-gray-50">
          <td class="p-2"><img v-if="item.picture" :src="img_source(item.picture)" class="w-10 h-10 object-cover rounded-lg" alt=""></td>
          <td class="p-2 text-xs text-gray-700">{{ item.name }}</td>
          <td class="p-2 text-xs text-gray-500">{{ (item.items || []).length }}</td>
          <td class="p-2 text-xs text-gray-500">{{ formatNumber(item.regular_price) }}</td>
          <td class="p-2 text-xs text-gray-500">{{ item.bundle_discount_percentage || 0 }}٪</td>
          <td class="p-2 text-xs font-bold text-gray-700">{{ formatNumber(item.price) }}</td>
          <td class="p-2 text-xs text-gray-500">{{ item.inventory_count != null ? item.inventory_count : '—' }}</td>
          <td class="p-2 text-xs">
            <span :class="item.active ? 'text-emerald-600' : 'text-red-500'">{{ item.active ? 'فعال' : 'غیرفعال' }}</span>
            <div v-if="!item.active && item.bundle_disabled_reason" class="text-[10px] text-red-400 mt-0.5">{{ item.bundle_disabled_reason }}</div>
          </td>
          <td class="p-2 flex gap-1">
            <button @click="toggleActive(item)" class="px-2 py-1 bg-gray-200 text-gray-700 text-[11px] rounded-lg">
              {{ item.active ? 'غیرفعال کن' : 'فعال کن' }}
            </button>
            <button @click="editBundle(item)" class="px-2 py-1 bg-gray-200 text-gray-700 text-[11px] rounded-lg">ویرایش</button>
            <button @click="deleteBundle(item)" class="px-2 py-1 bg-red-500 text-white text-[11px] rounded-lg">حذف</button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import BundleItemsSelect from "@/components/selects/BundleItemsSelect";
import CategoryTreeSelect from "@/modules/product_admin/CategoryTreeSelect";
import BrandSelect from "@/components/selects/BrandSelect";

const STORE_BASE_URL = "https://exuni.ir";

export default {
  name: "PackageBuilder",
  components: { BundleItemsSelect, CategoryTreeSelect, BrandSelect },
  data() {
    return {
      bundles: [],
      loading: false,
      saving: false,
      editingId: null,
      coverPreview: null,
      categorizations: [],
      form: this.emptyForm(),
    };
  },
  computed: {
    publicBundlesUrl() {
      return `${STORE_BASE_URL}/bundles`;
    },
    subtotal() {
      return (this.form.items || []).reduce((sum, i) => {
        const price = i.product ? Number(i.product.price || 0) : 0;
        return sum + price * (i.quantity || 0);
      }, 0);
    },
    discountAmount() {
      return this.subtotal * (Number(this.form.bundle_discount_percentage) || 0) / 100;
    },
    finalPrice() {
      return Math.max(0, this.subtotal - this.discountAmount);
    },
  },
  created() {
    this.setDocumentTitle && this.setDocumentTitle("پکیج ساز");
    this.fetchBundles();
    this.fetchCategorizations();
  },
  methods: {
    emptyForm() {
      return {
        name: "",
        bundle_discount_percentage: 0,
        inventory: 0,
        is_active: true,
        picture: null,
        items: [],
        categorization: [],
        brand: null,
      };
    },
    fetchCategorizations() {
      this.request({
        url: this.endpoint("product/categorizationTree"),
        method: "get",
        success: (data) => { this.categorizations = data || []; },
      });
    },
    formatNumber(val) {
      if (val === null || val === undefined) return "۰";
      return Number(val).toLocaleString("fa-IR");
    },
    resetForm() {
      this.editingId = null;
      this.form = this.emptyForm();
      this.coverPreview = null;
    },
    onCoverChange(e) {
      const file = e.target.files && e.target.files[0];
      this.form.picture = file || null;
      if (file) {
        const reader = new FileReader();
        reader.onload = (ev) => { this.coverPreview = ev.target.result; };
        reader.readAsDataURL(file);
      } else {
        this.coverPreview = null;
      }
    },
    copyPublicLink() {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.publicBundlesUrl);
      }
      this.notify && this.notify("لینک صفحه پکیج‌ها کپی شد", "success");
    },
    fetchBundles() {
      this.loading = true;
      this.request({
        url: this.endpoint("product/adminBundles"),
        method: "get",
        success: (data) => {
          this.bundles = (data && data.results) || data || [];
          this.loading = false;
        },
        error: () => { this.loading = false; },
      });
    },
    editBundle(item) {
      this.editingId = item.id;
      this.coverPreview = item.picture ? this.img_source(item.picture) : null;
      this.form = {
        name: item.name,
        bundle_discount_percentage: item.bundle_discount_percentage || 0,
        inventory: item.inventory_count || 0,
        is_active: item.active,
        picture: null,
        items: (item.items || []).map((row) => ({
          product_id: row.component.id,
          quantity: row.quantity,
          product: row.component,
        })),
        categorization: item.categorization || [],
        brand: item.brand ? item.brand.id : null,
      };
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    buildFormData() {
      const fd = new FormData();
      fd.append("name", this.form.name || "");
      fd.append("bundle_discount_percentage", this.form.bundle_discount_percentage || 0);
      fd.append("inventory", this.form.inventory || 0);
      fd.append("is_active", this.form.is_active ? "true" : "false");
      fd.append("items", JSON.stringify((this.form.items || []).map((i) => ({
        component_id: i.product_id,
        quantity: i.quantity,
      }))));
      fd.append("categorization_ids", JSON.stringify((this.form.categorization || []).map((c) => c.id)));
      fd.append("brand_id", this.form.brand || "");
      if (this.form.picture instanceof File) {
        fd.append("picture", this.form.picture);
      }
      return fd;
    },
    save() {
      if (!this.form.name) {
        this.notify && this.notify("نام پکیج الزامی است", "warning");
        return;
      }
      if (!this.form.items || !this.form.items.length) {
        this.notify && this.notify("حداقل یک محصول باید به پکیج اضافه بشه", "warning");
        return;
      }
      this.saving = true;
      const isEdit = !!this.editingId;
      this.request({
        url: this.endpoint(isEdit ? `product/adminBundle/${this.editingId}` : "product/adminBundles"),
        method: isEdit ? "put" : "post",
        data: this.buildFormData(),
        headers: { "Content-Type": "multipart/form-data" },
        success: () => {
          this.saving = false;
          this.notify && this.notify("ذخیره شد", "success");
          this.resetForm();
          this.fetchBundles();
        },
        error: (err) => {
          this.saving = false;
          const data = err && err.response && err.response.data;
          const message = (data && (data.items || data.name || data.brand_id || data.detail)) || "خطا در ذخیره‌سازی";
          this.notify && this.notify(typeof message === "string" ? message : JSON.stringify(message), "danger");
        },
      });
    },
    toggleActive(item) {
      const fd = new FormData();
      fd.append("is_active", (!item.active) ? "true" : "false");
      this.request({
        url: this.endpoint(`product/adminBundle/${item.id}`),
        method: "put",
        data: fd,
        headers: { "Content-Type": "multipart/form-data" },
        success: () => {
          item.active = !item.active;
          if (item.active) item.bundle_disabled_reason = null;
        },
        error: () => {
          this.notify && this.notify("خطا در تغییر وضعیت", "danger");
        },
      });
    },
    deleteBundle(item) {
      if (!window.confirm(`پکیج «${item.name}» حذف بشه؟`)) return;
      this.request({
        url: this.endpoint(`product/adminBundle/${item.id}`),
        method: "delete",
        success: () => {
          this.notify && this.notify("حذف شد", "success");
          if (this.editingId === item.id) this.resetForm();
          this.fetchBundles();
        },
        error: () => {
          this.notify && this.notify("خطا در حذف", "danger");
        },
      });
    },
  },
};
</script>
